<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <router-view />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import appConfigs from "@configs/app";
import AuthService from "@/modules/auth/service/auth.service";
import SessionManager from "@modules/auth/service/session.manager";
import { appLocalStorage } from "@utils/app.storage";
import DOMUtils from "@utils/dom.utils";
import { LAST_ACCESSED_USERS_MAX_LENGTH } from "@modules/app/service/app.constant";
import { useFeatureFlagStore } from "@/modules/feature-flag/use-feature-flag-store";
import { authStateChannel, STATE_CHANGE_ACTION } from "@/modules/auth/service/auth.state.channel";

const store = useStore();
const router = useRouter();

const sessionManager = new SessionManager({
  namespace: "session",
  sessionExpiredInMilliSeconds: appConfigs.userSessionExpiredDuration,
  sessionWarningInMilliSeconds: appConfigs.userSessionWarningDuration,
  showExpiredPrompt: true,
  onExpire: () => {
    AuthService.logout().then(() => {
      store.dispatch("auth/updateLogged");
    });
  },
  onExtend: () => {
    /* Empty Function */
  },
});

const headerScripts = ref(appConfigs.headerScripts);

const isLogged = computed(() => store.getters["auth/isLogged"]);
const userId = computed(() => store.getters["auth/userId"]);
const checkWholePageDirty = computed(() => store.getters["app/checkWholePageDirty"]);

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (checkWholePageDirty.value) {
    // Cancel the event as stated by the standard.
    event.preventDefault();
    // returnValue need to be set for chrome to bring up the leaving page confirmation
    event.returnValue = false;
  }
};

const updateLocalStorage = () => {
  let lastAccessedUsers: string[] = [];
  let lastUsedComponents: string[] = [];

  const lastAccessedUsersStr = appLocalStorage.getItem("lastAccessedUsers");
  if (lastAccessedUsersStr) {
    lastAccessedUsers = JSON.parse(lastAccessedUsersStr);
  }

  if (lastAccessedUsers.includes(userId.value)) {
    lastAccessedUsers.splice(lastAccessedUsers.indexOf(userId.value), 1);
  }
  lastAccessedUsers.push(userId.value);

  if (lastAccessedUsers.length > LAST_ACCESSED_USERS_MAX_LENGTH) {
    const removedUser = lastAccessedUsers.shift();
    Object.keys(localStorage)
      .filter((key) => key.endsWith(removedUser!))
      .forEach((key) => appLocalStorage.removeItem(key));
  }

  const lastUsedComponentsStr = appLocalStorage.getItem("lastUsedComponents");
  if (lastUsedComponentsStr) {
    lastUsedComponents = JSON.parse(lastUsedComponentsStr);
  }

  appLocalStorage.setItem("lastUsedComponents", lastUsedComponents);
  appLocalStorage.setItem("lastAccessedUsers", lastAccessedUsers);
};

const featureFlagStore = useFeatureFlagStore();

const refreshEnabledFeatures = async (): Promise<void> => {
  if (!isLogged.value) {
    clearEnabledFeatures();
    return;
  }

  await featureFlagStore.refreshEnabledFeatures();
};

const clearEnabledFeatures = () => {
  featureFlagStore.clearEnabledFeatures();
};

watch(isLogged, async (logged) => {
  if (logged) {
    sessionManager.init();
    window._SLS = window._SLS || {};
    window._SLS.sessionManager = sessionManager;

    updateLocalStorage();
    await refreshEnabledFeatures();
  }
});

onMounted(() => {
  if (!appConfigs.enableConsole) {
    if (!window.console) {
      window.console = {};
    }
    const methods = ["log", "debug", "warn", "info"];
    methods.forEach((method) => {
      console[method] = () => {
        /* Empty Function */
      };
    });
  }

  if (!window.beforeUnloadEventBinded) {
    window.beforeUnloadEventBinded = true;
    DOMUtils.addBeforeUnloadEventListener(handleBeforeUnload);
  }

  if (headerScripts.value && headerScripts.value.length) {
    headerScripts.value.forEach((script) => {
      const scriptEl = document.createElement("script");
      for (const key in script) {
        if (script[key]) {
          scriptEl.setAttribute(key, script[key]);
        }
      }

      document.head.appendChild(scriptEl);
    });
  }

  authStateChannel.on("stateChange", (data) => {
    if (isLogged.value) {
      if (data === STATE_CHANGE_ACTION.LOGOUT) {
        router.replace({
          name: "Logout",
        });
      } else if (data === STATE_CHANGE_ACTION.SWITCH_ACCOUNT) {
        window.location.reload();
      }
    }
  });

  refreshEnabledFeatures();
});

onBeforeUnmount(() => {
  appLocalStorage.setItem("lastUsedComponents", []);
  clearEnabledFeatures();

  authStateChannel.close();
});
</script>
